// TabularSectionEditor — табличная часть в форме объекта.
// ОФФЛАЙН-FIRST: запись через dataService (онлайн → сервер, ошибки бросаются;
// офлайн → очередь синхронизации). После каждой операции строки перезагружаются.
// Ошибки и офлайн-статус показываются явно. Никаких тихих фоллбэков.
import { confirmDialog, alertDialog } from '@/lib/dialogs';
import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { FormFieldRenderer, type FieldMeta } from '@/components/forms/FormFieldRenderer';
import { ReferencePickerField } from '@/components/forms/ReferencePickerField';
import { dataService } from '@/lib/dataService';
import { createLogger } from '@/lib/logger';
import type { ListFilter } from '@/types/formProperties';

const log = createLogger('TabularSectionEditor');

interface Column {
  name: string;
  dataType?: string;
  isRequired?: boolean;
  refTable?: string;
  /** Параметры выбора (как в 1С): статичные отборы формы выбора колонки */
  selectionParams?: ListFilter[];
  /** Связи параметров выбора (как в 1С): параметр ← колонка ТОЙ ЖЕ строки ТЧ */
  selectionLinks?: Array<{ param: string; field: string }>;
}

interface TabularSectionEditorProps {
  /** Имя физической таблицы ТЧ ({каталог}_{имяТЧ}) */
  tableName: string;
  /** Синоним для отображения */
  synonym?: string;
  /** Определения колонок */
  columns: Column[];
  /** id родительской записи (строки ТЧ привязываются через parent_id) */
  parentId?: string;
  /** Режим: только чтение */
  readOnly?: boolean;
  /** Доступность (enabled) */
  enabled?: boolean;
  /** Уведомить родителя об изменении */
  onUpdate?: () => void;
}

export function TabularSectionEditor({
  tableName, synonym, columns, parentId, readOnly, enabled = true, onUpdate,
}: TabularSectionEditorProps) {
  const [rows, setRows] = useState<Array<Record<string, unknown>>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [offline, setOffline] = useState(!dataService.isOnline());

  const disabled = !enabled || readOnly;

  const loadRows = useCallback(async () => {
    setLoading(true);
    setError(null);
    setOffline(!dataService.isOnline());
    // Офлайн: fetchAll тихо не падает — строки берём из кэша чтения.
    await dataService.fetchAll('public', tableName);
    const all = dataService.list('public', tableName) as Array<Record<string, unknown>>;
    // Только строки текущей записи (parent_id); у легаси-строк без parent_id
    // строки не показываем ни в одной записи.
    setRows(parentId ? all.filter((r) => r.parent_id === parentId) : all);
    setLoading(false);
  }, [tableName, parentId]);

  useEffect(() => {
    loadRows();
    // Реагируем на восстановление/потерю сети
    const onNet = () => loadRows();
    window.addEventListener('online', onNet);
    window.addEventListener('offline', onNet);
    return () => {
      window.removeEventListener('online', onNet);
      window.removeEventListener('offline', onNet);
    };
  }, [loadRows]);

  const handleAdd = async () => {
    setError(null);
    try {
      const newRow: Record<string, unknown> = {};
      for (const col of columns) {
        newRow[col.name] = col.dataType === 'boolean' ? false : '';
      }
      // Привязка строки к родительской записи (иначе строка видна во ВСЕХ элементах)
      if (parentId) newRow.parent_id = parentId;
      await dataService.create('public', tableName, newRow);
      await loadRows();
      onUpdate?.();
    } catch (e: any) {
      log.error('TS add failed', e);
      setError(e?.message || 'Ошибка добавления строки');
      // НЕ зовём loadRows() — она сбрасывает error в начале, и ошибка «исчезает»
    }
  };

  const handleCellChange = async (rowId: string, colName: string, value: unknown) => {
    setError(null);
    try {
      await dataService.update('public', tableName, rowId, { [colName]: value });
      await loadRows();
      onUpdate?.();
    } catch (e: any) {
      log.error('TS cell update failed', e);
      setError(e?.message || `Ошибка сохранения колонки «${colName}»`);
    }
  };

  const handleDelete = async (rowId: string) => {
    if (!await confirmDialog('Удалить строку?')) return;
    setError(null);
    try {
      await dataService.remove('public', tableName, rowId);
      await loadRows();
      onUpdate?.();
    } catch (e: any) {
      log.error('TS delete failed', e);
      setError(e?.message || 'Ошибка удаления строки');
    }
  };

  return (
    <div className={`space-y-2 border rounded-md p-3 ${disabled ? 'opacity-60 pointer-events-none' : ''}`}>
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-medium">{synonym || tableName}</h4>
        <div className="flex items-center gap-2">
          {offline && (
            <span className="text-[10px] text-amber-600 bg-amber-50 border border-amber-200 rounded px-2 py-0.5">
              офлайн — изменения в очереди
            </span>
          )}
          {!disabled && (
            <Button size="sm" variant="outline" onClick={handleAdd} disabled={loading}>
              + Добавить строку
            </Button>
          )}
        </div>
      </div>

      {error && (
        <div className="text-xs text-destructive bg-destructive/10 border border-destructive/20 rounded-md px-3 py-2">
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-xs text-muted-foreground p-2">Загрузка строк...</div>
      ) : rows.length === 0 ? (
        <div className="text-xs text-muted-foreground italic">Нет строк</div>
      ) : (
        <div className="space-y-1">
          {/* Заголовки */}
          <div className="flex gap-2 text-xs font-medium text-muted-foreground">
            {columns.map((col) => (
              <div key={col.name} className="flex-1 min-w-0">
                {col.name}
                {col.isRequired && <span className="text-destructive ml-0.5">*</span>}
              </div>
            ))}
            <div className="w-8" />
          </div>

          {/* Строки */}
          {rows.map((row) => (
            <div key={String(row.id)} className="flex gap-2 items-start">
              {columns.map((col) => {
                const dt = (col.dataType || 'text').toLowerCase();
                const isRef = (dt === 'uuid' || dt === 'reference') && !!col.refTable;
                return (
                  <div key={col.name} className="flex-1 min-w-0">
                    {isRef ? (
                      <ReferencePickerField
                        value={row[col.name] ?? ''}
                        refTable={col.refTable}
                        displayValue={String(row[col.name + '_presentation'] ?? '')}
                        selectionParams={col.selectionParams}
                        selectionLinks={col.selectionLinks}
                        contextValues={row}
                        onChange={(v) => handleCellChange(row.id as string, col.name, v)}
                        disabled={disabled}
                        compact
                      />
                    ) : (
                      <FormFieldRenderer
                        field={{
                          name: col.name,
                          label: '',
                          data_type: dt,
                          is_required: col.isRequired,
                        }}
                        value={row[col.name] ?? ''}
                        onChange={(v) => handleCellChange(row.id as string, col.name, v)}
                        compact
                        disabled={disabled}
                      />
                    )}
                  </div>
                );
              })}
              <div className="w-8">
                {!disabled && (
                  <Button size="sm" variant="ghost" className="h-7 px-1.5 text-destructive" onClick={() => handleDelete(row.id as string)}>
                    ✕
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
